import { escapeHtml } from '../lib/escape';
import {
  applyPreset,
  deletePreset,
  downloadPreset,
  importPresetFromFile,
  loadPresets,
  savePreset,
  type PresetData,
} from '../presets';
import {
  ExportValidationError,
  buildConsumerExport,
  downloadExport,
} from '../consumer-export';

let modalEl: HTMLElement | null = null;
let importInput: HTMLInputElement | null = null;

export function openPresetsModal(): void {
  ensureModal();
  renderPresetList();
  modalEl!.classList.remove('hidden');
}

function ensureModal(): void {
  if (modalEl) return;

  modalEl = document.createElement('div');
  modalEl.className = 'presets-modal hidden';
  modalEl.innerHTML = `
    <div class="presets-dialog">
      <div class="presets-dialog-header">
        <span class="presets-dialog-title">Preset Manager</span>
        <button type="button" class="presets-close-btn" aria-label="Close">×</button>
      </div>

      <div class="presets-save-section">
        <label class="presets-label">Save current state as preset</label>
        <div class="presets-save-row">
          <input
            type="text"
            class="presets-name-input"
            placeholder="Preset name…"
            maxlength="80"
          />
          <button type="button" class="presets-save-btn">Save</button>
        </div>
      </div>

      <div class="presets-divider"></div>

      <div class="presets-list-section">
        <div class="presets-list-header">
          <span class="presets-label">Saved Presets</span>
          <label class="presets-import-label" title="Import preset from JSON file">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Import JSON
            <input type="file" accept=".json,application/json" class="presets-import-input" />
          </label>
        </div>
        <div class="presets-list"></div>
      </div>

      <div class="presets-divider"></div>

      <div class="presets-export-section">
        <div class="presets-export-header">
          <span class="presets-label">Export for consumer</span>
          <button type="button" class="presets-export-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download consumer config (.zip)
          </button>
        </div>
        <p class="presets-export-hint">Emits shared/generationConfig.json plus the three PNGs, packaged for the label-stories repo.</p>
        <div class="presets-export-errors hidden"></div>
      </div>
    </div>
  `;

  document.body.appendChild(modalEl);

  // Close button
  const closeBtn = modalEl.querySelector<HTMLButtonElement>('.presets-close-btn');
  closeBtn?.addEventListener('click', closeModal);

  // Click outside to close
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) closeModal();
  });

  // Esc to close
  document.addEventListener('keydown', (e) => {
    if (modalEl?.classList.contains('hidden')) return;
    if (e.key === 'Escape') closeModal();
  });

  // Save button
  const saveBtn = modalEl.querySelector<HTMLButtonElement>('.presets-save-btn');
  const nameInput = modalEl.querySelector<HTMLInputElement>('.presets-name-input');
  saveBtn?.addEventListener('click', () => {
    const name = nameInput?.value.trim() ?? '';
    savePreset(name || 'Untitled Preset');
    if (nameInput) nameInput.value = '';
    renderPresetList();
  });
  nameInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') saveBtn?.click();
  });

  // Consumer export
  const exportBtn = modalEl.querySelector<HTMLButtonElement>('.presets-export-btn');
  const exportErrors = modalEl.querySelector<HTMLElement>('.presets-export-errors');
  exportBtn?.addEventListener('click', () => {
    void handleConsumerExport(exportBtn, exportErrors);
  });

  // Import input
  importInput = modalEl.querySelector<HTMLInputElement>('.presets-import-input');
  importInput?.addEventListener('change', () => {
    const file = importInput?.files?.[0];
    if (!file) return;
    void handleImport(file);
    if (importInput) importInput.value = '';
  });

  // Preset list delegated events
  const listEl = modalEl.querySelector<HTMLElement>('.presets-list');
  listEl?.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const loadBtn = target.closest<HTMLElement>('[data-action="load"]');
    if (loadBtn) {
      const id = loadBtn.dataset.id;
      if (id) handleLoad(id);
      return;
    }

    const dlBtn = target.closest<HTMLElement>('[data-action="download"]');
    if (dlBtn) {
      const id = dlBtn.dataset.id;
      if (id) handleDownload(id);
      return;
    }

    const delBtn = target.closest<HTMLElement>('[data-action="delete"]');
    if (delBtn) {
      const id = delBtn.dataset.id;
      if (id) handleDelete(id);
      return;
    }
  });
}

function renderPresetList(): void {
  const listEl = modalEl?.querySelector<HTMLElement>('.presets-list');
  if (!listEl) return;

  const presets = loadPresets();

  if (presets.length === 0) {
    listEl.innerHTML = `<p class="presets-empty">No saved presets yet.</p>`;
    return;
  }

  listEl.innerHTML = presets
    .map((p) => renderPresetRow(p))
    .join('');
}

function renderPresetRow(p: PresetData): string {
  const date = formatDate(p.savedAt);
  const qCount = p.questions.filter((q) => q.kind === 'choice').length;
  return `
    <div class="preset-row" data-id="${escapeHtml(p.id)}">
      <div class="preset-row-info">
        <span class="preset-row-name">${escapeHtml(p.name)}</span>
        <span class="preset-row-meta">${escapeHtml(date)} &middot; ${qCount} question${qCount !== 1 ? 's' : ''}</span>
      </div>
      <div class="preset-row-actions">
        <button type="button" class="preset-action-btn" data-action="load" data-id="${escapeHtml(p.id)}" title="Load preset">
          Load
        </button>
        <button type="button" class="preset-action-btn preset-action-icon" data-action="download" data-id="${escapeHtml(p.id)}" title="Download as JSON">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>
        <button type="button" class="preset-action-btn preset-action-delete" data-action="delete" data-id="${escapeHtml(p.id)}" title="Delete preset">×</button>
      </div>
    </div>
  `;
}

function handleLoad(id: string): void {
  const preset = loadPresets().find((p) => p.id === id);
  if (!preset) return;
  applyPreset(preset);
  closeModal();
}

function handleDownload(id: string): void {
  const preset = loadPresets().find((p) => p.id === id);
  if (preset) downloadPreset(preset);
}

function handleDelete(id: string): void {
  deletePreset(id);
  renderPresetList();
}

async function handleConsumerExport(
  btn: HTMLButtonElement,
  errorsEl: HTMLElement | null,
): Promise<void> {
  if (errorsEl) {
    errorsEl.classList.add('hidden');
    errorsEl.innerHTML = '';
  }
  btn.disabled = true;
  const originalLabel = btn.innerHTML;
  btn.textContent = 'Exporting…';
  try {
    const result = await buildConsumerExport();
    downloadExport(result);
  } catch (err) {
    const issues =
      err instanceof ExportValidationError
        ? err.issues
        : [err instanceof Error ? err.message : String(err)];
    if (errorsEl) {
      errorsEl.innerHTML = `
        <div class="presets-export-errors-title">Export blocked:</div>
        <ul>${issues.map((m) => `<li>${escapeHtml(m)}</li>`).join('')}</ul>
      `;
      errorsEl.classList.remove('hidden');
    } else {
      alert(`Export failed:\n${issues.join('\n')}`);
    }
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalLabel;
  }
}

async function handleImport(file: File): Promise<void> {
  try {
    const preset = await importPresetFromFile(file);
    // Save to localStorage then refresh list
    const existing = loadPresets();
    existing.unshift(preset);
    localStorage.setItem('espolon-presets', JSON.stringify(existing));
    renderPresetList();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    alert(`Could not import preset: ${msg}`);
  }
}

function closeModal(): void {
  modalEl?.classList.add('hidden');
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
